import { z } from "zod"

/**
 * DTO расширенной аналитики кабинета продавца (Этап 8: fd-analytics-02/03/04)
 * и конфигурации Яндекс.Метрики витрины (fd-analytics-05).
 *
 * Всё считается по реальным заказам магазина (orders.data->'groups') за
 * скользящее окно `days` дней по московскому календарю — ничего не выдумывается.
 * Продавец всегда видит только свой магазин: shopId резолвится из токена в
 * роуте, наружу параметром не принимается.
 */

// fd-analytics-02 — динамика продаж, категории, топ-товары, средний чек.

/** Точка дневного ряда продаж: оборот и число заказов за календарный день МСК. */
export type SalesDayPoint = { date: string; revenue: number; orders: number }

/** Срез продаж по категории товара (категория берётся из карточки по productId). */
export type SalesCategorySlice = { category: string; revenue: number; qty: number }

/**
 * Товар в топе продаж магазина за период.
 *
 * `photo` и `productId` — из снимка позиции заказа: в макетах V1 топ идёт с
 * миниатюрой товара, а клик ведёт в карточку. Оба поля необязательны: у старых
 * снимков заказа фото могло не быть, и подставлять чужое нельзя.
 */
export type SalesTopProduct = {
  name: string
  qty: number
  revenue: number
  productId?: string
  photo?: string
}

export type SellerSalesAnalytics = {
  shopId: string
  /** Ширина окна в днях (после клампа). */
  days: number
  /** Первый и последний день окна — календарные даты МСК (YYYY-MM-DD). */
  periodStart: string
  periodEnd: string
  /** Число оплаченных заказов магазина за период. */
  ordersCount: number
  /** Оборот (subtotal + доставка) по оплаченным группам магазина. */
  gmv: number
  /**
   * Тот же оборот без доставки — сумма товаров (subtotal групп). В карточке
   * «Оборот» идёт мелкой строкой под основным числом (правки заказчика 26.09).
   */
  gmvWithoutDelivery: number
  /** Средний чек = gmv / ordersCount (0 без заказов). */
  avgCheck: number
  /** Плотный ряд по дням окна — ноль в дни без продаж. */
  byDay: SalesDayPoint[]
  /** Категории по убыванию выручки. Без категории — «Без категории». */
  byCategory: SalesCategorySlice[]
  /** Топ товаров по выручке. */
  topProducts: SalesTopProduct[]
}

// fd-analytics-03 — воронка конверсии.
// Ступени «показы → просмотр → корзина → заказано → выкуплено» считаются тем же
// расчётом, что на вкладке «Обзор» (один источник), в единицах «посетитель ×
// товар». Разбивка по статусам заказов осталась рядом - в `statusFunnel`.

/** Ступень воронки: ключ, человекочитаемая подпись, число заказов. */
export type ConversionStage = { key: string; label: string; count: number }

/** Разбивка заказов магазина по статусу. */
export type ConversionStatusCount = { status: string; count: number }

export type SellerConversionAnalytics = {
  shopId: string
  days: number
  periodStart: string
  periodEnd: string
  /** Всего заказов магазина за период (любой статус). */
  ordersTotal: number
  /**
   * Воронка - те же пять ступеней и те же числа, что `funnel` вкладки «Обзор»
   * за тот же период. Конверсия считается для каждой ступени из предыдущей.
   */
  funnel: OverviewFunnelStep[]
  /**
   * Единица счёта ступеней: пара «посетитель × товар» за период. Один человек,
   * трижды открывший карточку товара, - один просмотр.
   */
  funnelUnit: "visitor_product"
  /** Пришли ли за период события витрины (иначе первые три ступени - «нет замера»). */
  storefrontEventsConnected: boolean
  /** Заказы по статусам: создан → оплачен → завершён (прежняя воронка экрана). */
  statusFunnel: ConversionStage[]
  /** Заказы, дошедшие до оплаты (любой статус кроме pending/declined/cancelled). */
  paid: number
  /** Отменённые (cancelled) + отклонённые оплаты (declined). */
  cancelled: number
  /** Возвраты — заказы в статусе return_requested. */
  returned: number
  /** Доля оплаченных от всех заказов (0..1). */
  paidShare: number
  /** Доля отменённых/отклонённых от всех заказов (0..1). */
  cancelledShare: number
  /** Доля возвратов от оплаченных (0..1). */
  returnShare: number
  /** Полная разбивка по статусам — для детализации на фронте. */
  byStatus: ConversionStatusCount[]
}

// fd-analytics-04 — география заказов по адресу доставки.

/** Группа географии: город либо регион. */
export type GeoBucket = { name: string; orders: number; revenue: number }

export type SellerGeoAnalytics = {
  shopId: string
  days: number
  periodStart: string
  periodEnd: string
  /** Оплаченные заказы магазина за период. */
  ordersCount: number
  /** Оборот по этим заказам. */
  gmv: number
  /** Города по обороту (data.address.city). Без города - «Не указан». */
  byCity: GeoBucket[]
  /**
   * Субъекты РФ по обороту (fd-analytics-04): регион из адреса заказа, у
   * старых заказов без него - по городу через справочник. Не опознан -
   * «Не указан». Сумма по регионам равна сумме по городам.
   */
  byRegion: GeoBucket[]
  /**
   * Федеральные округа («Приволжский ФО»…) по справочнику субъектов. Регион не
   * опознан или адрес вне РФ - «Не указан». Сумма равна сумме по регионам.
   */
  byFederalDistrict: GeoBucket[]
}

// fd-analytics-05 — Яндекс.Метрика витрины.

/** Публичная конфигурация счётчика: null — счётчик не задан. */
export const MetrikaConfigSchema = z.object({
  counterId: z.string().nullable(),
})
export type MetrikaConfig = z.infer<typeof MetrikaConfigSchema>

/** Тело админского PATCH: пустая строка/пробелы/null снимают счётчик. */
export const MetrikaConfigPatchSchema = z.object({
  counterId: z
    .string()
    .trim()
    .max(32)
    .regex(/^\d*$/, "counterId — числовой идентификатор счётчика")
    .nullable()
    .optional(),
})
export type MetrikaConfigPatch = z.infer<typeof MetrikaConfigPatchSchema>

/**
 * Цели витрины в Метрике: идентификатор `reachGoal` и название цели в счётчике.
 * Витрина отправляет ровно эти идентификаторы (client-web/lib/metrika-ecommerce),
 * консоль показывает по ним конверсию и умеет завести недостающие.
 */
export const STOREFRONT_METRIKA_GOALS = [
  { identifier: "registration", name: "Регистрация" },
  { identifier: "promo_applied", name: "Применение промокода" },
  { identifier: "checkout", name: "Оформление заказа" },
  { identifier: "payment", name: "Оплата заказа" },
  { identifier: "become_seller", name: "Заявка «Стать продавцом»" },
] as const

/** Источник трафика за период: визиты и доля от всех визитов (0..1). */
export type MetrikaSource = { name: string; visits: number; share: number }

/** Цель счётчика за период: достижения и конверсия (доля визитов с целью, 0..1). */
export type MetrikaGoalStat = {
  id: number
  name: string
  /** Идентификатор `reachGoal`, если это цель «JavaScript-событие». */
  identifier: string | null
  reaches: number
  conversion: number
}

/**
 * Блок «Метрика» на дашборде консоли - отчёт Reporting API за период.
 *
 * `status`: `ok` - отчёт получен; `no_counter` - номер счётчика не задан;
 * `no_token` - нет OAuth-токена отчётов в окружении шлюза; `error` - Метрика
 * ответила ошибкой (`error` - её текст). Числа - как в интерфейсе Метрики за те
 * же даты: полная точность, атрибуция источников - «последний значимый переход».
 */
export type MetrikaReport = {
  status: "ok" | "no_counter" | "no_token" | "error"
  counterId: string | null
  periodStart: string
  periodEnd: string
  visits: number
  users: number
  pageviews: number
  /** Отказы, доля визитов 0..1. */
  bounceRate: number
  sources: MetrikaSource[]
  goals: MetrikaGoalStat[]
  /** Цели витрины, которых нет в счётчике: без них Метрика не считает достижения. */
  missingGoals: { identifier: string; name: string }[]
  /** Электронная коммерция; null - Метрика не отдала (не включена в счётчике). */
  ecommerce: { purchases: number; revenue: number } | null
  /** Когда отчёт получен у Метрики (кеш на час). */
  fetchedAt: string | null
  error?: string
}

// ── Вкладка «Обзор» раздела «Продажи» (макеты V1, кадр 02) ──
//
// Часть воронки на этом экране опирается на события витрины (показы, просмотры
// карточек, добавления в корзину). Веб-витрина шлёт их пачками в
// `POST /discovery/events/batch`; мобильное приложение пока молчит. Поэтому
// каждая ступень несёт флаг `measured`: если за окно событий не пришло,
// `count` равен null и интерфейс пишет «нет замера» вместо числа. Рисовать в
// этих местах любое число — значит показать продавцу выдумку.

/** Ступень зигзаг-воронки. `count: null` — замер не подключён. */
export type OverviewFunnelStep = {
  key: string
  label: string
  count: number | null
  /** Есть ли источник данных под эту ступень. */
  measured: boolean
  /** Сколько отвалилось на переходе к этой ступени (null, если считать не из чего). */
  lost: number | null
  /** Доля отвала к предыдущей ступени, 0..1. */
  lostShare: number | null
  /** Конверсия из предыдущей ступени, 0..1. */
  conversion: number | null
}

/** Строка таблицы «Воронка» по возрастным группам покупателей. */
export type OverviewFunnelAgeRow = {
  /** Возрастная группа: «18 – 24», …, «65 +», «Не указан». */
  age: string
  /** Визиты — из событий витрины; null, если замера нет. */
  visits: number | null
  /** Целевые посетители по шагам: просмотр товара → … → покупка. */
  steps: { key: string; targeted: number | null; cr: number | null }[]
}

/**
 * Разбивки таблицы «Воронка» — выпадающий список над таблицей (Айталы
 * 14.10.2026): «Общее, Тип устройства, Город, Пол, Возраст, Пользователи».
 */
export const FUNNEL_BREAKDOWNS = ["total", "device", "city", "gender", "age", "users"] as const
export type FunnelBreakdown = (typeof FUNNEL_BREAKDOWNS)[number]

/** Строка таблицы «Воронка» в выбранной разбивке. Первая строка — «Итого». */
export type OverviewFunnelRow = {
  /** Ключ группы: «total», «mobile», «Москва», «female», «18 – 24», «new»… */
  key: string
  /** Подпись строки: «Итого», «Смартфон», «Москва», «Женский», «Не указан». */
  label: string
  /** Визиты — из событий витрины; null, если замера нет. */
  visits: number | null
  steps: { key: string; targeted: number | null; cr: number | null }[]
}

/** Разрез «Количество заказов» по диапазонам чека. */
export type OverviewCheckBucket = { key: string; label: string; orders: number }

/** День в графике «Коэффициент возврата товаров». */
export type OverviewReturnsDay = {
  date: string
  orders: number
  returns: number
  defects: number
}

/** Точка комбо-графика «Продажи и план»: столбик — факт, линия — план. */
export type OverviewSalesPoint = { date: string; sales: number; plan: number | null }

export type SellerOverviewAnalytics = {
  shopId: string
  periodStart: string
  periodEnd: string
  /** Продажи и план за период. */
  sales: {
    /** Продажи с доставкой (subtotal + deliveryFee оплаченных групп). */
    value: number
    /** Те же продажи без доставки — только товары. */
    valueWithoutDelivery: number
    /** План на период (из настройки «шестерёнка»); null — план не задан. */
    plan: number | null
    /** Прирост к предыдущему окну той же ширины, %. null — сравнивать не с чем. */
    deltaPct: number | null
    series: OverviewSalesPoint[]
  }
  /** Количество заказов, средний чек, число покупателей и разрез по чекам. */
  orders: {
    count: number
    avgCheck: number
    customers: number
    avgCheckDeltaPct: number | null
    customersDeltaPct: number | null
    byCheck: OverviewCheckBucket[]
  }
  /** Возвраты и брак к числу заказов. */
  returns: {
    orders: number
    returns: number
    returnsShare: number
    defects: number
    defectsShare: number
    byDay: OverviewReturnsDay[]
  }
  /** Зигзаг-воронка из пяти шагов. */
  funnel: OverviewFunnelStep[]
  /** Воронка по возрастам покупателя (users.birth_date). */
  funnelByAge: OverviewFunnelAgeRow[]
  /**
   * Таблица «Воронка» во всех разбивках сразу: переключение списка над
   * таблицей не ходит на шлюз второй раз. Первая строка каждой — «Итого».
   */
  funnelBreakdowns: Record<FunnelBreakdown, OverviewFunnelRow[]>
  /**
   * Подключён ли сбор событий витрины. false — ступени «Показы», «Просмотр
   * карточки» и «Добавление в корзину» не измеряются, и интерфейс говорит об
   * этом прямо, а не подменяет их нулями.
   */
  storefrontEventsConnected: boolean
}

/** План продаж магазина: сумма на календарный месяц. */
export type SellerSalesPlan = {
  shopId: string
  /** Месячный план в рублях; null — план не задан. */
  monthlyPlan: number | null
  updatedAt: string | null
}
